import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Container, SelectChangeEvent, Typography } from '@mui/material';

import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import Loadable from '@components/Loadable/Loadable';
import { HighRiskSupplement } from '@models/highRiskSupplement';
import { handleGetHighRiskSupplementDetails } from '@store/features/highRiskSupplementDetails/actions';
import {
  highRiskSupplementsDetailsErrorSelector,
  highRiskSupplementsDetailsIdSelector,
  highRiskSupplementsDetailsIsLoadingSelector,
  highRiskSupplementsDetailsSelector,
} from '@store/features/highRiskSupplementDetails/selectors';
import { removeHighRiskSupplementsDetails } from '@store/features/highRiskSupplementDetails/slice';
import { handleGetHighRiskSupplementsList } from '@store/features/highRiskSupplements/actions';
import {
  removeHighRiskSupplementsList,
  removeHighRiskSupplementsSearchData,
} from '@store/features/highRiskSupplements/slice';
import { handleSearchHighRiskSupplements } from '@store/features/highRiskSupplementsSearch/actions';
import {
  highRiskSupplementSearchPaginationSelector,
  highRiskSupplementSearchValueSelector,
} from '@store/features/highRiskSupplementsSearch/selectors';
import useDispatch from '@store/hooks/useDispatch';

import HRSDetailsCard from './components/HRSDetailsCard/HRSDetailsCard';
import HRSFilters from './components/HRSFilters/HRSFilters';
import HRSFullList from './components/HRSFullList/HRSFullList';
import HRSSearch from './components/HRSSearch/HRSSearch';
import HRSSearchList from './components/HRSSearchList/HRSSearchList';
import HRSTermsAndConditionsModal from './components/HRSTermsAndConditionsModal/HRSTermsAndConditionsModal';
import { createSearchPayload } from './utils/utils';

import './style.scss';

enum ListViewMode {
  SearchList,
  AllDataList,
}

const HighRiskSupplementsPage: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [firstViewModalOpen, setFirstViewModalOpen] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [viewMode, setViewMode] = useState<ListViewMode>(ListViewMode.AllDataList);

  const detailsIsLoading = useSelector(highRiskSupplementsDetailsIsLoadingSelector);
  const detailsError = useSelector(highRiskSupplementsDetailsErrorSelector);
  const details = useSelector(highRiskSupplementsDetailsSelector);
  const activeEntryId = useSelector(highRiskSupplementsDetailsIdSelector);
  const searchPagination = useSelector(highRiskSupplementSearchPaginationSelector);
  const preservedSearchValue = useSelector(highRiskSupplementSearchValueSelector);

  const closeModal = useCallback(() => {
    setFirstViewModalOpen(false);
  }, []);

  const handleCategoriesFilterChange = useCallback((event: SelectChangeEvent, child: ReactNode) => {
    setSelectedCategory(event.target.value);
  }, []);

  const clearSelectedCategory = useCallback(() => {
    setSelectedCategory('');
  }, []);

  const handleAllListPagination = useCallback(
    (page: number) => {
      dispatch(removeHighRiskSupplementsDetails());
      dispatch(handleGetHighRiskSupplementsList({ page, selectedCategory }));
    },
    [dispatch, selectedCategory],
  );

  const handleSearchPagination = useCallback(
    (page: number) => {
      dispatch(removeHighRiskSupplementsDetails());
      dispatch(
        handleSearchHighRiskSupplements(
          createSearchPayload(page, preservedSearchValue, selectedCategory),
        ),
      );
    },
    [dispatch, preservedSearchValue, selectedCategory],
  );

  const handleFetchRiskSupplementsList = useCallback(() => {
    dispatch(handleGetHighRiskSupplementsList({ page: 1, selectedCategory }));
  }, [dispatch, selectedCategory]);

  const handleFetchRiskSupplementsSearchList = useCallback(() => {
    dispatch(
      handleSearchHighRiskSupplements(
        createSearchPayload(
          searchPagination?.page as number,
          preservedSearchValue,
          selectedCategory,
        ),
      ),
    );
  }, [dispatch, selectedCategory, searchPagination, preservedSearchValue]);

  const fetchRiskSupplementDetails = useCallback(
    (id: number | null) => {
      if (typeof id === 'number') {
        dispatch(handleGetHighRiskSupplementDetails(id));
      }
    },
    [dispatch],
  );

  const onRetryFetchRiskSupplementDetails = useCallback(
    () => fetchRiskSupplementDetails(activeEntryId),
    [fetchRiskSupplementDetails, activeEntryId],
  );

  const handleRiskSupplementClick = useCallback(
    (data: HighRiskSupplement) => {
      fetchRiskSupplementDetails(data.id);
    },
    [fetchRiskSupplementDetails],
  );

  const handleSearchByDebounceInput = useCallback(
    (searchValue: string) => {
      if (!searchValue) {
        setViewMode(ListViewMode.AllDataList);
        dispatch(removeHighRiskSupplementsSearchData());
        dispatch(removeHighRiskSupplementsDetails());
        return;
      }
      dispatch(
        handleSearchHighRiskSupplements(createSearchPayload(1, searchValue, selectedCategory)),
      );
    },
    [searchPagination, dispatch, selectedCategory],
  );

  const handleSearchKeyDown = useCallback((event: any) => {
    if (event?.keyCode === 13 && event?.target?.value && event.target.value.length > 2) {
      dispatch(removeHighRiskSupplementsDetails());
      setViewMode(ListViewMode.SearchList);
    }
  }, []);

  useEffect(() => {
    if (!firstViewModalOpen && viewMode === ListViewMode.AllDataList) {
      handleFetchRiskSupplementsList();
    }
  }, [firstViewModalOpen, handleFetchRiskSupplementsList, viewMode]);

  useEffect(() => {
    dispatch(removeHighRiskSupplementsDetails());
    if (viewMode === ListViewMode.SearchList) {
      handleSearchPagination(1);
    }
  }, [selectedCategory]);

  useEffect(
    () => () => {
      dispatch(removeHighRiskSupplementsList());
      dispatch(removeHighRiskSupplementsSearchData());
      dispatch(removeHighRiskSupplementsDetails());
    },
    [],
  );

  return (
    <Container maxWidth="lg" className="high-risk-supplements__main">
      <Typography variant="h1" className="high-risk-supplements__page-title">
        {t('highRiskSupplements.pageTitle')}
      </Typography>

      <Box className="high-risk-supplements__wrapper">
        <Box className="high-risk-supplements__block high-risk-supplements__block-list">
          <CardWithShadow className="high-risk-supplements__block-card">
            <Box className="high-risk-supplements__search-panel">
              <Typography className="high-risk-supplements__block-title">
                {t('highRiskSupplements.listBlockTitle')}
              </Typography>
              <Box className="high-risk-supplements__search-block-wrapper">
                <HRSSearch
                  title={t('highRiskSupplements.supplementName')}
                  onSearch={handleSearchByDebounceInput}
                  onKeyDown={handleSearchKeyDown}
                />
              </Box>
            </Box>
            <Box className="high-risk-supplements__filter-panel">
              <HRSFilters
                value={selectedCategory}
                onChange={handleCategoriesFilterChange}
                onClear={clearSelectedCategory}
              />
            </Box>
            {viewMode === ListViewMode.AllDataList && (
              <HRSFullList
                onFetchEntriesList={handleFetchRiskSupplementsList}
                onEntryClick={handleRiskSupplementClick}
                onPaginationChange={handleAllListPagination}
              />
            )}
            {viewMode === ListViewMode.SearchList && (
              <HRSSearchList
                onFetchEntriesList={handleFetchRiskSupplementsSearchList}
                onEntryClick={handleRiskSupplementClick}
                onPaginationChange={handleSearchPagination}
              />
            )}
          </CardWithShadow>
        </Box>
        <Box className="high-risk-supplements__block">
          <CardWithShadow className="high-risk-supplements__block-card">
            <Box className="high-risk-supplements__details-title-wrapper">
              <Typography component="h2" className="high-risk-supplements__details-title">
                {t('highRiskSupplements.infoBlockTitle')}
              </Typography>
            </Box>
            <Box className="high-risk-supplements__content">
              {!activeEntryId && (
                <Typography className="high-risk-supplements__content-text">
                  {t('highRiskSupplements.infoBlockDefaultText')}
                </Typography>
              )}
              {Boolean(activeEntryId) && (
                <Loadable
                  isLoading={detailsIsLoading}
                  error={detailsError && t('errors.highRiskSupplementsDetailsError')}
                  onRetry={onRetryFetchRiskSupplementDetails}
                >
                  {details && <HRSDetailsCard details={details} />}
                </Loadable>
              )}
            </Box>
          </CardWithShadow>
        </Box>
      </Box>

      {firstViewModalOpen && <HRSTermsAndConditionsModal onClose={closeModal} />}
    </Container>
  );
};

export default HighRiskSupplementsPage;
